/**
 * MetaQuestAdapter — v10.10.0 (Fase 2 — WebXR immersive-ar real)
 *
 * Implementa `XRDeviceAdapter` para Meta Quest 2/3/Pro utilizando WebXR
 * en el navegador nativo del visor. Requiere:
 *   - `navigator.xr` presente (Meta Browser 26+ o Wolvic)
 *   - Session `immersive-ar` con features: hand-tracking, hit-test, anchors
 *
 * Render mínimo con Three.js:
 *   - Cámara passthrough (delegada al compositor XR del sistema)
 *   - Escena con luces básicas + reticle de hit-test
 *   - Loop de hand-tracking: detecta gesto "pinch" (index tip cerca del
 *     thumb tip) y emite `xr:hand_pinch` al backend vía `onMarker`.
 *
 * En dispositivos donde `immersive-ar` no está disponible el adaptador
 * arroja error explícito para que el bootstrap caiga al WebPWAAdapter.
 */
import * as THREE from 'three';
import { XRDeviceAdapter } from './XRDeviceAdapter';

const PINCH_DISTANCE_M = 0.025; // 2.5 cm
const PINCH_COOLDOWN_MS = 400;
const HAND_JOINT_INDEX_TIP = 'index-finger-tip';
const HAND_JOINT_THUMB_TIP = 'thumb-tip';

export class MetaQuestAdapter extends XRDeviceAdapter {
  static get id() { return 'meta_quest'; }

  constructor(opts = {}) {
    super({ deviceType: opts.deviceType || 'mixed_reality', deviceName: opts.deviceName || 'Meta Quest' });
    this._session = null;
    this._renderer = null;
    this._scene = null;
    this._camera = null;
    this._reticle = null;
    this._hitTestSource = null;
    this._referenceSpace = null;
    this._container = null;
    this._handlers = null;
    this._lastPinchAt = { left: 0, right: 0 };
    this._anchors = new Map(); // id -> XRAnchor
    this._raf = null;
  }

  async capabilities() {
    const caps = {
      camera_supported: false,               // el compositor XR maneja la cámara
      marker_tracking_supported: false,      // se hará por hit-test + anchors (no QR)
      webxr_supported: false,
      depth_supported: false,
      spatial_anchor_supported: false,
      hand_tracking_supported: false,
      voice_supported: 'speechSynthesis' in window,
    };
    if (typeof navigator === 'undefined' || !navigator.xr) return caps;
    try {
      caps.webxr_supported = !!(await navigator.xr.isSessionSupported('immersive-ar'));
      // Los flags a continuación no se pueden verificar antes de crear la
      // sesión; los reportamos como TRUE cuando `immersive-ar` está OK,
      // basándonos en que Meta Quest Browser los soporta desde v26.
      if (caps.webxr_supported) {
        caps.hand_tracking_supported = true;
        caps.spatial_anchor_supported = true;
        caps.depth_supported = true;
        caps.marker_tracking_supported = true; // proxy: anchors + hit-test
      }
    } catch { /* noop */ }
    return caps;
  }

  attachContainer(el) { this._container = el; }
  getVideoElement() { return null; }

  async startMarkerTracking(handlers = {}) {
    if (!navigator.xr) throw new Error('WebXR no disponible en este dispositivo');
    const ok = await navigator.xr.isSessionSupported('immersive-ar').catch(() => false);
    if (!ok) throw new Error('immersive-ar no está soportado en este visor');

    this._handlers = handlers;

    // 1) Renderer
    const canvas = document.createElement('canvas');
    if (this._container) this._container.appendChild(canvas);
    const gl = canvas.getContext('webgl2', { xrCompatible: true, alpha: true });
    const renderer = new THREE.WebGLRenderer({ canvas, context: gl, antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    renderer.xr.enabled = true;
    this._renderer = renderer;

    // 2) Scene + camera + reticle
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.05, 20);
    scene.add(new THREE.HemisphereLight(0xffffff, 0x666666, 1.2));

    const reticleGeom = new THREE.RingGeometry(0.05, 0.06, 32).rotateX(-Math.PI / 2);
    const reticleMat = new THREE.MeshBasicMaterial({ color: 0x06b6d4, transparent: true, opacity: 0.8 });
    const reticle = new THREE.Mesh(reticleGeom, reticleMat);
    reticle.matrixAutoUpdate = false;
    reticle.visible = false;
    scene.add(reticle);

    this._scene = scene;
    this._camera = camera;
    this._reticle = reticle;

    // 3) Session
    const requiredFeatures = ['hit-test'];
    const optionalFeatures = ['hand-tracking', 'anchors', 'local-floor', 'depth-sensing'];
    const session = await navigator.xr.requestSession('immersive-ar', {
      requiredFeatures,
      optionalFeatures,
    });
    this._session = session;
    session.addEventListener('end', () => this._cleanupSession());

    await renderer.xr.setSession(session);

    // 4) Reference spaces + hit-test
    try {
      this._referenceSpace = await session.requestReferenceSpace('local-floor');
    } catch {
      this._referenceSpace = await session.requestReferenceSpace('local');
    }
    try {
      const viewerSpace = await session.requestReferenceSpace('viewer');
      this._hitTestSource = await session.requestHitTestSource({ space: viewerSpace });
    } catch { /* hit-test opcional */ }

    // 5) Select event → anchor + emit xr:marker_anchor
    session.addEventListener('select', async () => {
      if (!this._reticle.visible) return;
      const pos = new THREE.Vector3().setFromMatrixPosition(this._reticle.matrix);
      const markerId = `ANCHOR_${Date.now().toString(36).toUpperCase()}`;
      try { this._emit('xr:marker_anchor', { marker_id: markerId, position: pos.toArray() }); }
      catch { /* noop */ }
      // Marker visible como cubo
      const box = new THREE.Mesh(
        new THREE.BoxGeometry(0.06, 0.06, 0.06),
        new THREE.MeshStandardMaterial({ color: 0x22d3ee, roughness: 0.4, metalness: 0.1 })
      );
      box.position.copy(pos);
      this._scene.add(box);
    });

    // 6) Render loop
    renderer.setAnimationLoop((_time, frame) => this._onFrame(frame));
  }

  _emit(marker_id_or_event, payload = {}) {
    if (!this._handlers?.onMarker) return;
    try {
      this._handlers.onMarker({
        raw: marker_id_or_event,
        marker_id: marker_id_or_event,
        adapter: 'meta_quest',
        at: new Date().toISOString(),
        ...payload,
      });
    } catch { /* noop */ }
  }

  _onFrame(frame) {
    if (!frame || !this._session) return;
    const refSpace = this._referenceSpace;
    if (!refSpace) return;

    // Hit-test → posiciona el reticle
    if (this._hitTestSource) {
      try {
        const results = frame.getHitTestResults(this._hitTestSource);
        if (results.length > 0) {
          const pose = results[0].getPose(refSpace);
          if (pose) {
            this._reticle.visible = true;
            this._reticle.matrix.fromArray(pose.transform.matrix);
          }
        } else {
          this._reticle.visible = false;
        }
      } catch { /* noop */ }
    }

    // Hand tracking → pinch detection
    try {
      for (const source of this._session.inputSources) {
        if (!source.hand) continue;
        const handedness = source.handedness || 'right';
        const indexTip = source.hand.get(HAND_JOINT_INDEX_TIP);
        const thumbTip = source.hand.get(HAND_JOINT_THUMB_TIP);
        if (!indexTip || !thumbTip) continue;
        const p1 = frame.getJointPose(indexTip, refSpace);
        const p2 = frame.getJointPose(thumbTip, refSpace);
        if (!p1 || !p2) continue;
        const dx = p1.transform.position.x - p2.transform.position.x;
        const dy = p1.transform.position.y - p2.transform.position.y;
        const dz = p1.transform.position.z - p2.transform.position.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < PINCH_DISTANCE_M) {
          const now = performance.now();
          if (now - (this._lastPinchAt[handedness] || 0) > PINCH_COOLDOWN_MS) {
            this._lastPinchAt[handedness] = now;
            this._emit(`PINCH_${handedness.toUpperCase()}`, {
              gesture: 'pinch',
              hand: handedness,
              distance_m: dist,
              position: [p1.transform.position.x, p1.transform.position.y, p1.transform.position.z],
            });
          }
        }
      }
    } catch { /* noop */ }

    this._renderer.render(this._scene, this._camera);
  }

  async stopMarkerTracking() {
    try {
      if (this._session) await this._session.end();
    } catch { /* noop */ }
    this._cleanupSession();
  }

  _cleanupSession() {
    try { this._renderer?.setAnimationLoop?.(null); } catch { /* noop */ }
    try {
      this._renderer?.dispose?.();
      if (this._renderer?.domElement?.parentNode) this._renderer.domElement.parentNode.removeChild(this._renderer.domElement);
    } catch { /* noop */ }
    this._hitTestSource = null;
    this._referenceSpace = null;
    this._session = null;
    this._renderer = null;
    this._scene = null;
    this._camera = null;
    this._reticle = null;
    this._anchors.clear();
  }
}
